import { Check, FileJson } from 'lucide-react';
import { useId, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { canAt } from '@/admin-area/place/scopes';
import { useAdministrationScopesOptional } from '@/admin-area/place/AdministrationScopesProvider';
import { PageHead } from '@/admin-area/ui/PageHead';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { Button } from '@/components/ui/button';
import { FormFeedback } from '@/components/ui/form-feedback';
import { useKeyedPendingWrite } from '@/lib/use-pending-write';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';
import {
  type ImportCounts,
  type InviteOutcome,
  importReferenceLists,
  inviteRequester,
  setIgnoredEmail,
} from './api';
import { readLastImport, writeLastImport, type LastImport } from './last-import';
import { type ImportSummary, parseImportFile } from './parse-import-file';

type Loaded =
  | { readonly kind: 'none' }
  | { readonly kind: 'invalid'; readonly reason: 'not-json' | 'not-version-1' }
  | {
      readonly kind: 'ready';
      readonly summary: ImportSummary;
      readonly body: Record<string, unknown>;
    };

type Result =
  | { readonly kind: 'none' }
  | { readonly kind: 'sending' }
  | {
      readonly kind: 'done';
      readonly counts: ImportCounts;
      readonly unmatched: readonly string[];
      readonly ignored: readonly string[];
    }
  | { readonly kind: 'failed'; readonly status: number; readonly message: string | null };

function useImportFlow() {
  const [loaded, setLoaded] = useState<Loaded>({ kind: 'none' });
  const [result, setResult] = useState<Result>({ kind: 'none' });
  const [ignoreFailed, setIgnoreFailed] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const { isPending, run } = useKeyedPendingWrite();
  const [invites, setInvites] = useState<Readonly<Record<string, 'sending' | InviteOutcome>>>({});
  const context = useActiveContextOptional();
  const scopes = useAdministrationScopesOptional();
  const organizationId = context?.active?.organizationId;
  const [last, setLast] = useState<LastImport | null>(() => readLastImport(organizationId));
  // It is kept per organization: switching to another one shows that one's last import, if any.
  const [lastFor, setLastFor] = useState(organizationId);
  if (lastFor !== organizationId) {
    setLastFor(organizationId);
    setLast(readLastImport(organizationId));
  }
  // «Invita» shows to whom the administration area would show «Invita persona». Where that is not
  // known yet it shows, and a refusal says so (the server decides).
  const canInvite = mayInvite(organizationId, context?.active?.tenantId, scopes);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setResult({ kind: 'none' });
    setFileName(file?.name ?? null);
    if (file === undefined) {
      setLoaded({ kind: 'none' });
      return;
    }
    const parsed = parseImportFile(await file.text());
    setLoaded(
      parsed.ok
        ? { kind: 'ready', summary: parsed.summary, body: parsed.body }
        : { kind: 'invalid', reason: parsed.reason },
    );
  }

  async function send(body: Record<string, unknown>) {
    setResult({ kind: 'sending' });
    const outcome = await importReferenceLists(body);
    const record: LastImport = outcome.ok
      ? {
          at: new Date().toISOString(),
          ok: true,
          cards: outcome.counts.cards,
          vehicles: outcome.counts.vehicles,
          suppliers: outcome.counts.suppliers,
        }
      : { at: new Date().toISOString(), ok: false };
    writeLastImport(organizationId, record);
    setLast(record);
    setResult(
      outcome.ok
        ? {
            kind: 'done',
            counts: outcome.counts,
            unmatched: outcome.counts.unmatchedEmails,
            ignored: outcome.counts.ignoredEmails,
          }
        : { kind: 'failed', status: outcome.status, message: outcome.message },
    );
    // Sent (or refused): the file's content is not kept.
    if (outcome.ok) setLoaded({ kind: 'none' });
  }

  /** «Ignora» / «Annulla»: moves the email between the two lists once the server has it. */
  function toggleIgnored(email: string, ignored: boolean) {
    return run(email, async () => {
      setIgnoreFailed(false);
      if (!(await setIgnoredEmail(email, ignored))) {
        setIgnoreFailed(true);
        return;
      }
      setResult((current) => {
        if (current.kind !== 'done') return current;
        const without = (list: readonly string[]) => list.filter((item) => item !== email);
        const sorted = (list: readonly string[]) => [...list, email].sort();
        return ignored
          ? { ...current, unmatched: without(current.unmatched), ignored: sorted(current.ignored) }
          : { ...current, unmatched: sorted(current.unmatched), ignored: without(current.ignored) };
      });
    });
  }

  /** «Invita»: the administration area's invitation, as the caller, with the «Richiedente» role. */
  async function invite(email: string) {
    if (organizationId === undefined || invites[email] === 'sending') return;
    setInvites((current) => ({ ...current, [email]: 'sending' }));
    const outcome = await inviteRequester(organizationId, email);
    setInvites((current) => ({ ...current, [email]: outcome }));
  }

  return {
    loaded,
    result,
    last,
    invites,
    canInvite,
    ignoreFailed,
    isPending,
    fileName,
    onFile,
    send,
    toggleIgnored,
    invite,
  };
}

function FilePicker({
  inputId,
  fileName,
  onFile,
}: {
  readonly inputId: string;
  readonly fileName: string | null;
  readonly onFile: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  const { t } = useTranslation('expenses');
  const fileRef = useRef<HTMLInputElement | null>(null);
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="text-sm font-medium">
        {t('import.file')}
      </label>
      <input
        id={inputId}
        ref={fileRef}
        type="file"
        accept=".json,application/json"
        onChange={onFile}
        className="hidden"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
          <FileJson aria-hidden="true" />
          {t('import.chooseFile')}
        </Button>
        {fileName === null ? null : (
          <span className="text-muted-foreground text-sm break-all">{fileName}</span>
        )}
      </div>
    </div>
  );
}

/**
 * «Importa da nexpense»: the administrator picks the export file, sees what it holds, then sends
 * it (`POST /v1/expenses/reference/import`). The file is read in memory only: its content is kept
 * nowhere and logged nowhere, and it is dropped once sent.
 */
export function ImportReferencePage() {
  const { t } = useTranslation('expenses');
  const titleId = useId();
  const inputId = useId();
  const permissions = useExpensesPermissions();
  const flow = useImportFlow();
  const { loaded, result, invites, canInvite, ignoreFailed, isPending } = flow;
  if (permissions.status === 'loading') return null;
  if (!holds(permissions, EXPENSES_MANAGE)) {
    return (
      <StateMessage
        role="alert"
        icon={FileJson}
        title={t('import.noAccess')}
        text={t('import.noAccessText')}
      />
    );
  }

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <PageHead title={t('import.title')} summary={t('import.summary')} titleId={titleId} />
      <WhatHappens last={flow.last} />
      <FilePicker
        inputId={inputId}
        fileName={flow.fileName}
        onFile={(event) => void flow.onFile(event)}
      />

      {loaded.kind === 'invalid' ? (
        <FormFeedback tone="error">
          {loaded.reason === 'not-json' ? t('import.notJson') : t('import.notVersion1')}
        </FormFeedback>
      ) : null}

      {loaded.kind === 'ready' ? (
        <ReadySummary
          summary={loaded.summary}
          sending={result.kind === 'sending'}
          onSend={() => void flow.send(loaded.body)}
        />
      ) : null}

      {result.kind === 'failed' ? (
        <FormFeedback tone="error">
          {result.status === 403 ? t('import.forbidden') : (result.message ?? t('import.failed'))}
        </FormFeedback>
      ) : null}

      {result.kind === 'done' ? (
        <ImportDone
          result={result}
          invites={invites}
          canInvite={canInvite}
          ignoreFailed={ignoreFailed}
          isPending={isPending}
          onInvite={(email) => void flow.invite(email)}
          onToggle={(email, ignored) => void flow.toggleIgnored(email, ignored)}
        />
      ) : null}
    </section>
  );
}

function ReadySummary({
  summary,
  sending,
  onSend,
}: {
  readonly summary: ImportSummary;
  readonly sending: boolean;
  readonly onSend: () => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-base font-semibold">{t('import.contains')}</h3>
      <ul className="text-sm">
        <li>{t('import.version', { version: summary.version })}</li>
        <li>
          {t('import.cards', {
            company: summary.companyCards,
            personal: summary.personalCards,
          })}
        </li>
        <li>{t('import.vehicles', { count: summary.vehicles })}</li>
        <li>{t('import.suppliers', { count: summary.suppliers })}</li>
      </ul>
      <div>
        <Button disabled={sending} onClick={() => onSend()}>
          {t('import.submit')}
        </Button>
      </div>
    </div>
  );
}

type DoneResult = Extract<Result, { readonly kind: 'done' }>;
type Invites = Readonly<Record<string, 'sending' | InviteOutcome>>;

function mayInvite(
  organizationId: string | undefined,
  tenantId: string | undefined,
  scopes: ReturnType<typeof useAdministrationScopesOptional>,
): boolean {
  if (organizationId === undefined) return false;
  if (scopes === undefined || scopes.status !== 'ready') return true;
  return (
    canAt({ kind: 'organization', id: organizationId }, scopes.scopes, tenantId)?.invite === true
  );
}

function UnmatchedList({
  emails,
  invites,
  canInvite,
  isPending,
  onInvite,
  onToggle,
}: {
  readonly emails: readonly string[];
  readonly invites: Invites;
  readonly canInvite: boolean;
  readonly isPending: (email: string) => boolean;
  readonly onInvite: (email: string) => void;
  readonly onToggle: (email: string, ignored: boolean) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div className="text-sm">
      <p>{t('import.unmatched')}</p>
      <ul className="flex flex-col gap-1 pt-1">
        {emails.map((email) => (
          <li key={email} className="flex flex-wrap items-center justify-between gap-2">
            <span className="break-all">{email}</span>
            <span className="flex flex-wrap items-center gap-2">
              <InviteControl
                state={invites[email]}
                enabled={canInvite}
                email={email}
                onInvite={() => onInvite(email)}
              />
              <Button
                variant="outline"
                size="sm"
                aria-label={t('import.ignoreEmail', { email })}
                disabled={isPending(email)}
                onClick={() => onToggle(email, true)}
              >
                {t('import.ignore')}
              </Button>
            </span>
          </li>
        ))}
      </ul>
      {Object.values(invites).includes('invited') ? (
        <p className="text-muted-foreground pt-1 text-[13px]">{t('import.invitedNote')}</p>
      ) : null}
    </div>
  );
}

function IgnoredList({
  emails,
  isPending,
  onToggle,
}: {
  readonly emails: readonly string[];
  readonly isPending: (email: string) => boolean;
  readonly onToggle: (email: string, ignored: boolean) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div className="text-sm">
      <p>{t('import.ignored')}</p>
      <ul className="flex flex-col gap-1 pt-1">
        {emails.map((email) => (
          <li key={email} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-muted-foreground break-all">{email}</span>
            <Button
              variant="outline"
              size="sm"
              aria-label={t('import.undoIgnoreEmail', { email })}
              disabled={isPending(email)}
              onClick={() => onToggle(email, false)}
            >
              {t('import.undoIgnore')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ImportDone({
  result,
  invites,
  canInvite,
  ignoreFailed,
  isPending,
  onInvite,
  onToggle,
}: {
  readonly result: DoneResult;
  readonly invites: Invites;
  readonly canInvite: boolean;
  readonly ignoreFailed: boolean;
  readonly isPending: (email: string) => boolean;
  readonly onInvite: (email: string) => void;
  readonly onToggle: (email: string, ignored: boolean) => void;
}) {
  const { t } = useTranslation('expenses');
  const pending =
    result.counts.pendingCards + result.counts.pendingCardUsers + result.counts.pendingVehicles;
  return (
    <div className="flex flex-col gap-2" role="status">
      <h3 className="text-base font-semibold">{t('import.done')}</h3>
      <ul className="text-sm">
        <li>
          {t('import.doneCards', { count: result.counts.cards, users: result.counts.cardUsers })}
        </li>
        <li>{t('import.doneVehicles', { count: result.counts.vehicles })}</li>
        <li>{t('import.doneSuppliers', { count: result.counts.suppliers })}</li>
      </ul>
      {pending > 0 ? <p className="text-sm">{t('import.pending', { count: pending })}</p> : null}
      {result.unmatched.length > 0 ? (
        <UnmatchedList
          emails={result.unmatched}
          invites={invites}
          canInvite={canInvite}
          isPending={isPending}
          onInvite={onInvite}
          onToggle={onToggle}
        />
      ) : null}
      {result.ignored.length > 0 ? (
        <IgnoredList emails={result.ignored} isPending={isPending} onToggle={onToggle} />
      ) : null}
      {ignoreFailed ? <FormFeedback tone="error">{t('import.ignoreFailed')}</FormFeedback> : null}
      {result.counts.otherMethodLabels.length > 0 ? (
        <div className="text-sm">
          <p>{t('import.otherMethods')}</p>
          <ul className="list-disc pl-5">
            {result.counts.otherMethodLabels.map((label) => (
              <li key={label} className="break-all">
                {label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="text-muted-foreground text-sm">{t('import.deleteFile')}</p>
    </div>
  );
}

/** The «Invita» button of an unmatched email, or what became of it. */
function InviteControl({
  state,
  enabled,
  email,
  onInvite,
}: {
  readonly state: 'sending' | InviteOutcome | undefined;
  readonly enabled: boolean;
  readonly email: string;
  readonly onInvite: () => void;
}) {
  const { t } = useTranslation('expenses');
  const outcome = 'text-muted-foreground text-[13px]';
  if (state === 'invited') {
    return (
      <span role="status" className={`${outcome} inline-flex items-center gap-1`}>
        <Check aria-hidden="true" className="size-3.5" />
        {t('import.invited')}
      </span>
    );
  }
  if (state === 'already-pending') {
    return (
      <span role="status" className={outcome}>
        {t('import.invitePending')}
      </span>
    );
  }
  if (state === 'forbidden') {
    return (
      <span role="status" className={outcome}>
        {t('import.inviteForbidden')}
      </span>
    );
  }
  if (!enabled) return null;
  return (
    <>
      {state === 'failed' ? (
        <span role="alert" className="text-destructive text-[13px]">
          {t('import.inviteFailed')}
        </span>
      ) : null}
      <Button
        variant="outline"
        size="sm"
        disabled={state === 'sending'}
        aria-label={t('import.inviteEmail', { email })}
        onClick={onInvite}
      >
        {state === 'sending' ? t('import.inviting') : t('import.invite')}
      </Button>
    </>
  );
}

/** Before the file is chosen: what an import does, and the last one started from this browser. */
function WhatHappens({ last }: { readonly last: LastImport | null }) {
  const { t, i18n } = useTranslation('expenses');
  return (
    <div className="border-border bg-card flex flex-col gap-2 rounded-xl border p-4">
      <h3 className="text-base font-semibold">{t('import.whatHappens')}</h3>
      <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
        <li>{t('import.happens.adds')}</li>
        <li>{t('import.happens.people')}</li>
        <li>{t('import.happens.file')}</li>
      </ul>
      <p className="text-sm" data-testid="last-import">
        {last === null
          ? t('import.lastNone')
          : t(last.ok ? 'import.lastDone' : 'import.lastFailed', {
              date: new Date(last.at).toLocaleString(i18n.resolvedLanguage, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }),
              cards: last.cards ?? 0,
              vehicles: last.vehicles ?? 0,
              suppliers: last.suppliers ?? 0,
            })}
      </p>
    </div>
  );
}
